import {Box} from "@mui/system";
import _ from "lodash";
import {useEffect, useState} from "react";
import {LoadingComponent} from "../../../components/LoadingComponent";
import {RoutesGrid} from "../../../components/Routes/RoutesGrid";
import {useAuth} from "../../../firebase/firebaseAuth";
import {ClimbigEventType, ClimbingEvent, FOR_ALL, getDifficultyFor} from "../../../models/ClimbingEvent";
import {
    DifficultyCategory,
    getAllUserRouteStatus,
    recalculateScore,
    StatusLabelAndColour,
    UserClimbingEvent,
    UserClimbingRoute,
    UserRouteStatus
} from "../../../models/UserClimbingEvent";
import {UserData} from "../../../models/UserData";
import {createNewUserEvent, findUserEventWithId, updateUserEvent} from "../../../services/events/UserEventService";
import {UserEventDetailsComponent} from "../userEventData/UserEventDetailsComponent";
import {SimplifiedResultsComponent} from "./beta/SimplifiedResultsComponent";
import {UserEventStatisticComponent} from "./UserEventStatisticComponent";
import {useTranslation} from "react-i18next";
import {SavingResultsComponent} from "./SavingResultsComponent";
import {SponsorComponent} from "./SponsorsComponent";
import {Typography} from "@mui/material";
import {PickBestRoute} from "./PickBestRoute";
import {CompetitionInfoComponent} from "./CompetitionInfoComponent";
import {EventRouteSelectMenu} from "./EventRouteSelectMenu";
import {EventResultComponent} from "./EventInfoComponent";

type Props = {
    event: ClimbingEvent
}

type AnchorAndId = {
    anchor: HTMLElement,
    id: number
}

export const EventCompetingComponent: React.FC<Props> = ({event}) => {
    const {t} = useTranslation()
    const dificulties = getDifficultyFor(event.type, event.showGrade);
    const {user, userData, updateUserData} = useAuth()
    const [userClimbingEvent, setUserClimbingEvent] = useState<UserClimbingEvent>();
    const [anchorAndId, setAnchorAndId] = useState<null | AnchorAndId>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const handleMenu = (htmlEvent: React.MouseEvent<HTMLElement>, routeId?: number) => {
        setAnchorAndId({
            anchor: htmlEvent.currentTarget,
            id: routeId || event.routes.length + 1
        });
    };

    useEffect(() => {
        if (user && event && userData) {
            if (!userClimbingEvent || event.name !== userClimbingEvent.eventId) {
                findUserEventWithId(event.name, user.uid)
                    .then(
                        (userClimbingEvent) => {
                            if (userClimbingEvent) {
                                setUserClimbingEvent(userClimbingEvent)
                            } else {
                                const newUserClimbingEvent = new UserClimbingEvent(event.name, event.type, user.uid, userData.name, userData.email, userData.birthYear, userData.sex, [], 0, 0, DifficultyCategory.LIGHT)
                                createNewUserEvent(newUserClimbingEvent)
                                    .then(() => setUserClimbingEvent(newUserClimbingEvent))
                            }
                        }
                    ).then(() => setLoading(false))
            } else {
                addEventToUserListIfNotExists(event.name, userData)
                addUserDetailsToEventIfDifferent(userData, userClimbingEvent)
            }
        }
        // eslint-disable-next-line
    }, [event, user, userData, userClimbingEvent]);

    const routes = (event.type !== ClimbigEventType.BOULDER && !event.showGrade) ? event.routes.filter((route) => route.dificulty === userClimbingEvent?.category || route.dificulty === FOR_ALL) : event.routes;
    const addEventToUserListIfNotExists = (eventId: string, userData: UserData) => {
        if (!(userData.eventIds && userData.eventIds.includes(eventId))) {
            const newUserData = _.clone(userData)
            newUserData.eventIds = _.union(newUserData.eventIds, [event.name])
            updateUserData(newUserData)
        }
    }
    const addUserDetailsToEventIfDifferent = (userData: UserData, userClimbingEvent: UserClimbingEvent) => {
        if (userData.name !== userClimbingEvent.name || userData.email !== userClimbingEvent.email || userData.birthYear !== userClimbingEvent.birthYear || userData.sex !== userClimbingEvent.sex) {
            handleUpdateUserEvent({
                ...userClimbingEvent,
                name: userData.name,
                email: userData.email,
                birthYear: userData.birthYear,
                sex: userData.sex
            })
        }
    }
    const handleClose = () => {
        setAnchorAndId(null);
    };

    const updateRoutesArray = (status: UserRouteStatus): UserClimbingRoute[] => {
        if (anchorAndId && userClimbingEvent) {
            const foundRoute = userClimbingEvent.climbingRoutes.find(r => r.id === anchorAndId.id)
            if (foundRoute) {
                return userClimbingEvent.climbingRoutes.map(r => {
                    if (r.id === anchorAndId.id) {
                        return {...r, status}
                    }
                    return r;
                })
            } else {
                return userClimbingEvent.climbingRoutes.concat({id: anchorAndId.id, status})
            }
        }
        return []
    }
    const setStatusOnRoute = (status: UserRouteStatus) => {
        if (userClimbingEvent && anchorAndId && user) {
            const updatedRoute = updateRoutesArray(status);
            const newUserClimbingEvent = {...userClimbingEvent, climbingRoutes: updatedRoute} as UserClimbingEvent
            recalculateScore(newUserClimbingEvent, event)
            handleUpdateUserEvent(newUserClimbingEvent)
            handleClose()
        }
    }
    const handleUpdateUserEvent = (userEvent: UserClimbingEvent) => {
        if (user) {
            setSaving(true)
            updateUserEvent(userEvent, user.uid).then(() => setSaving(false))
            setUserClimbingEvent(userEvent)
        }
    }

    const getColorAndStatusForId = (id: number): StatusLabelAndColour | undefined => {
        if (userClimbingEvent) {
            const foundRouteStatus = userClimbingEvent.climbingRoutes.find(r => r.id === id)?.status || UserRouteStatus.NOTHING
            if (foundRouteStatus) {
                return getAllUserRouteStatus(event).find(r => r.status === foundRouteStatus);
            }
        }
        return undefined
    }

    return (
        <Box width="80%">
            {event.type !== ClimbigEventType.BOULDER && !event.useStartAndEndDate &&
                <CompetitionInfoComponent event={event} selectedGroup={userClimbingEvent?.competitionUserData?.group}
                                          handleClose={handleClose} clicked={!!anchorAndId} menuElement={
                    <EventRouteSelectMenu anchor={anchorAndId?.anchor} handleClose={handleClose}
                                          setStatusOnRoute={setStatusOnRoute}
                                          event={event}/>
                }/>}
            {(event.type === ClimbigEventType.BOULDER || event.useStartAndEndDate) &&
                <EventResultComponent event={event} menuElement={
                    <EventRouteSelectMenu anchor={anchorAndId?.anchor} handleClose={handleClose}
                                          setStatusOnRoute={setStatusOnRoute} event={event}/>
                }/>
            }
            {userClimbingEvent && !loading &&
                <>
                    <SponsorComponent/>
                    <UserEventDetailsComponent event={event} userEvent={userClimbingEvent}
                                               handleUpdate={handleUpdateUserEvent}/>

                    <UserEventStatisticComponent event={event} userEvent={userClimbingEvent}/>
                    <RoutesGrid dificulties={dificulties} routes={routes}
                                routesToNrOfClimbes={event.routesToNrOfClimbes}
                                handleMenu={handleMenu}
                                getColorForId={getColorAndStatusForId} addNewRouteButton={false}
                                hideDificulty={event.type !== ClimbigEventType.BOULDER && !event.showGrade}/>
                    <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" m={2}>
                        <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column"
                             width={250}
                             gap={2}>

                            <PickBestRoute userClimbingEvent={userClimbingEvent} climbingEvent={event}
                                           handleBestRouteChange={(id) => handleUpdateUserEvent({
                                               ...userClimbingEvent,
                                               bestRouteId: id
                                           })}
                                           handleNrOfClimbsOnHardestChange={(nr) => handleUpdateUserEvent({
                                               ...userClimbingEvent,
                                               nrOfClimbsOnHardest: nr
                                           })}
                                           handleNrOfEntryChange={(nr) => handleUpdateUserEvent({
                                               ...userClimbingEvent,
                                               nrOfEntry: nr
                                           })}
                            />
                            <SavingResultsComponent saving={saving}
                                                    resendEvent={() => handleUpdateUserEvent(userClimbingEvent)}/>
                            {event.liveResults &&
                                <SimplifiedResultsComponent userEvent={userClimbingEvent}/>
                            }
                        </Box>
                    </Box>
                    <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" mt={2}>
                        <Typography variant="h6" color="primary">{t('event.rules.title')}</Typography>
                        <Typography variant="body1">{t('event.rules.text')}</Typography>
                    </Box>


                </>}
            {(!userClimbingEvent || loading) && <LoadingComponent message={t('loading.data')}/>}


        </Box>)
}
